import {
  Box,
  Button,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import numeral from 'numeral'
import { Fragment, useMemo, useState } from 'react'
import { IconButton, Select, SkeletonText } from 'utoniq-ui'

import { ItemDetailBoxFragment, Maybe } from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { ResaleItem } from '@/components/organisms'
import { StorageImage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { generateCreditCardLogoPath } from '@/core/creditCard'
import { getItemSkuPrice } from '@/core/sku'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  loading: boolean
  selectableItemCount: number
  cardNumber?: string
  brand: string
  item: Maybe<ItemDetailBoxFragment>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: (quantity: number) => Promise<void>
}

export const ResaleApplicationCreateTemplate: Template<State, Action> = ({
  state,
  onSubmit,
}) => {
  const { t } = useTranslation('resaleApplicationCreate')
  const { onClick: onClickHelp } = useBrowserLink(getHelpUrl('resaleApply'))
  const { routerPages, router, generatePath } = useRouter<{
    itemId: string
  }>()

  return (
    <BaseLayout
      margin
      header={{
        back: true,
        defaultBackPage: generatePath(
          routerPages.ResaleApplicationCreatePrecaution,
          {
            itemId: router.query.itemId,
          }
        ),
        title: t('title', 'リセール応募'),
        right: (
          <IconButton.Help onClick={onClickHelp} edge="end" color="primary" />
        ),
      }}
    >
      {state.loading || !state.item ? (
        <ApplicationProceedSkeletonCell />
      ) : (
        <>
          <Typography variant="h4" sx={{ py: 5 }}>
            {t('resaleInfo', '応募枚数・お支払い方法選択')}
          </Typography>
          <ApplicationProceedContentCell
            item={state.item}
            brand={state.brand}
            cardNumber={state.cardNumber}
            selectableItemCount={state.selectableItemCount}
            onSubmit={onSubmit}
          />
        </>
      )}
    </BaseLayout>
  )
}

const ApplicationProceedSkeletonCell: Cell = () => {
  return (
    <CellFrame>
      <Box sx={{ py: 5 }}>
        <SkeletonText variant="h4" width="50%" />
      </Box>
      <Stack spacing={3} sx={{ pt: '42px' }}>
        <SkeletonText variant="h4" />
        <SkeletonText variant="body2" />
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={3}
        sx={{ pt: '22px' }}
      >
        <Skeleton variant="rectangular" width="50%" height={40} />
        <SkeletonText variant="caption" width="25%" />
      </Stack>
      <Stack sx={{ pt: 9 }}>
        <Stack sx={{ bgcolor: 'grey.50' }}>
          <Skeleton
            variant="rectangular"
            height={20}
            width="100%"
            sx={{ bgcolor: 'grey.100', my: '6px' }}
          />
        </Stack>
        <SkeletonText variant="body2" numberOfLine={2} lastWidth="25%" />
      </Stack>
      <Box sx={{ pt: 5 }}>
        <Divider />
      </Box>
      <Stack sx={{ pt: 9 }}>
        <Stack sx={{ bgcolor: 'grey.50' }}>
          <Skeleton
            variant="rectangular"
            height={20}
            width="100%"
            sx={{ bgcolor: 'grey.100', my: '6px' }}
          />
        </Stack>
        <SkeletonText variant="body2" numberOfLine={2} />
      </Stack>
      <Stack spacing={2} sx={{ pt: '34px' }}>
        <Stack direction="row" justifyContent="space-between">
          <SkeletonText variant="small" width="30%" />
          <SkeletonText variant="small" width="30%" />
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between">
          <SkeletonText variant="small" width="30%" />
          <SkeletonText variant="small" width="30%" />
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between">
          <SkeletonText variant="small" width="30%" />
          <SkeletonText variant="small" width="30%" />
        </Stack>
        <Stack alignItems="center" sx={{ pt: 8, pb: 6 }}>
          <Skeleton
            variant="rectangular"
            height={44}
            width={212}
            sx={{ borderRadius: 34 }}
          />
          <SkeletonText variant="caption" width="15%" sx={{ mt: 2 }} />
        </Stack>
      </Stack>
    </CellFrame>
  )
}

type ApplicationProceedContentCellProps = {
  item: State['item']
  selectableItemCount: State['selectableItemCount']
  cardNumber: State['cardNumber']
  brand: State['brand']
  onSubmit: Action['onSubmit']
}
const ApplicationProceedContentCell: Cell<ApplicationProceedContentCellProps> =
  ({ item, selectableItemCount, cardNumber, brand, onSubmit }) => {
    const { t } = useTranslation('resaleApplicationCreate')
    const { routerPages, router, generatePath } = useRouter<{
      itemId: string
    }>()
    const [selected, setSelected] = useState<string>('1')
    const { price: itemPrice } = useMemo(() => getItemSkuPrice(item), [item])
    // 代金
    const price = itemPrice * +selected
    // システム手数料
    const purchaseFee = useMemo(
      () => item?.metadata?.ticket?.resaleFee?.purchaseFee ?? 0,
      [item]
    )

    const totalAmount = useMemo(() => {
      if (!itemPrice) {
        return 0
      }
      return numeral(price + purchaseFee).format('0,0')
    }, [itemPrice, selected])

    const data = [
      {
        label: t('ticketPrice', '代金'),
        value: `￥${numeral(price).format('0,0')}`,
      },
      {
        label: t('transferFee', 'システム手数料'),
        value: `￥${numeral(purchaseFee).format('0,0')}`,
      },
    ]

    return (
      <CellFrame>
        <ResaleItem title={t('selectTicketNumber')}>
          <Stack spacing={2}>
            <Typography variant="body2">
              {t('selectTicketNumberToSell', '応募枚数を指定してください。', {
                number: selectableItemCount.toString(),
              })}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Select
                listItems={[...Array(selectableItemCount).keys()].map(
                  index => ({
                    value: (index + 1).toString(),
                    label: (index + 1).toString(),
                  })
                )}
                value={selected}
                onChange={value => setSelected(value)}
                formControlProps={{
                  sx: { minWidth: 100, bgcolor: 'grey.50', minHeight: 'auto' },
                }}
              />
              <Typography variant="caption">{t('sheet')}</Typography>
            </Stack>
          </Stack>
        </ResaleItem>
        <ResaleItem title={t('paymentInfo')}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="subtitle1" color="text.secondary">
              {t('creditCardInfo')}
            </Typography>
            {!cardNumber && (
              <Typography
                variant="subtitle1"
                color="primary.main"
                onClick={() =>
                  router.push(routerPages.PaymentMethodSetting, {
                    goBackPath: routerPages.ResaleApplicationCreate,
                  })
                }
              >
                {t('register')}
              </Typography>
            )}
          </Stack>
          {!cardNumber ? (
            <Typography variant="caption" color="text.hint">
              {t('cardInfoNotRegister')}
            </Typography>
          ) : (
            <Stack direction="row" alignItems="center" spacing={3}>
              <StorageImage
                width={40}
                height={32}
                src={generateCreditCardLogoPath(brand)}
                target="common"
              />
              <Typography variant="body2">
                {t('last4Digit', { number: cardNumber })}
              </Typography>
            </Stack>
          )}
        </ResaleItem>
        <ResaleItem title={t('totalPayment', 'お支払い総額')}>
          <Stack spacing={3}>
            <Stack>
              {data.map(({ value, label }, index) => (
                <Fragment key={index}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ py: 2 }}
                  >
                    <Typography variant="subtitle1" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="body2">{value}</Typography>
                  </Stack>
                  <Divider />
                </Fragment>
              ))}
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ py: 2 }}
              >
                <Typography variant="subtitle1" color="text.secondary">
                  {t('total')}
                </Typography>
                <Typography variant="h6">¥{totalAmount}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </ResaleItem>
        <Stack spacing={3} alignItems="center" sx={{ pb: 6 }}>
          <Button
            size="large"
            sx={{ width: 212 }}
            disabled={!cardNumber}
            onClick={() => onSubmit(Number(selected))}
          >
            {t('submit', '確認する')}
          </Button>
          <Button
            variant="text"
            sx={{ width: 212 }}
            onClick={() =>
              router.goBack(
                generatePath(routerPages.ResaleApplicationCreatePrecaution, {
                  itemId: router.query.itemId,
                })
              )
            }
          >
            {t('back', '戻る')}
          </Button>
        </Stack>
      </CellFrame>
    )
  }
